import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Category } from 'shared/types/product';
import Checkbox from 'components/commercetools-ui/atoms/checkbox';
import { calculateArea, cleanString } from 'helpers/utils/util';
import { useRouter } from 'i18n/routing';
import { useFeatures } from 'providers/feature-flag';
import { useProductList } from '../../context';

interface GroupedAccordionProps {
  groupedAttributes: any[] | undefined;
  activeRefineList: string[];
  refine: (sourceIdentifier: string, key: string) => void;
  categories?: Category[];
  tagsToShow?: string;
  /* Facet pagination labels, authored in the CMS. */
  loadMoreText?: string;
  seeLessText?: string;
}

const getFirstOpenKey = (groups: any[]) => {
  if (!Array.isArray(groups)) return undefined;
  const found = groups.find((g) => Array.isArray(g?.terms) && g.terms.length > 0);
  return found ? getGroupKey(found) : undefined;
};

const getGroupKey = (group: any) => group?.identifier || group?.key || cleanString(group?.label ?? '');

/* Facet terms are revealed FACETS_PAGE_SIZE at a time behind a "Load more" control. */
export const FACETS_PAGE_SIZE = 10;

const normalizeValue = (value: any) => (value ?? '').toString().trim().toLowerCase();

const hasValidGroupTerms = (groups: any[], key: string) => {
  const group = groups.find((g) => getGroupKey(g) === key);
  return Boolean(group && Array.isArray(group?.terms) && group.terms.length > 0);
};

const buildCategoryIdToNameMap = (categories?: Category[]) => {
  const lookup: Record<string, string> = {};
  const traverse = (cats: any[]) => {
    cats?.forEach((cat) => {
      if (cat.id && cat.name) {
        lookup[cat.id.toString().trim().toLowerCase()] = cat.name;
      }
      if (cat.descendants) traverse(cat.descendants);
    });
  };
  traverse(categories || []);
  return lookup;
};

const collectAllCategoryTags = (categories?: Category[]) => {
  const tags = new Set<string>();
  const traverse = (cats: any[]) => {
    cats?.forEach((cat) => {
      if (cat.categoryTags) {
        if (Array.isArray(cat.categoryTags)) {
          cat.categoryTags.forEach((tag: string) => tags.add(tag.toLowerCase()));
        } else {
          Object.keys(cat.categoryTags).forEach((tag) => tags.add(tag.toLowerCase()));
        }
      }
      if (cat.descendants) traverse(cat.descendants);
    });
  };
  traverse(categories || []);
  return tags;
};

const aggregateOnlyTagsToShow = (terms: any[], tagsToShowLower: string[]) => {
  const map = new Map<string, any>();
  const result: any[] = [];

  for (const term of terms) {
    const labelLower = normalizeValue(term?.label);

    if (tagsToShowLower.includes(labelLower)) {
      if (!map.has(labelLower)) {
        const aggregated = {
          label: term.label,
          key: term.label,
          refineTargets: [] as Array<{ sourceIdentifier: string; key: any; label: any }>,
        };
        map.set(labelLower, aggregated);
        result.push(aggregated);
      }

      map.get(labelLower).refineTargets.push({
        label: term.label,
        key: term.key,
        sourceIdentifier: term.sourceIdentifier,
      });
    } else {
      result.push(term);
    }
  }

  return result;
};

const getWishlistProductIds = (wishlist: any) => {
  const productIds: string[] = [];

  wishlist?.lineItems?.forEach((lineItem: any) => {
    if (lineItem?.productId) {
      productIds.push(lineItem.productId);
    }
  });

  return productIds;
};

const applyAggregatedRefinement = ({
  isChecked,
  activeTargets,
  targets,
  isTermActive,
  refine,
}: {
  isChecked: boolean;
  activeTargets: any[];
  targets: any[];
  isTermActive: (term: any) => boolean;
  refine: (sourceIdentifier: string, key: string) => void;
}) => {
  if (isChecked) {
    activeTargets.forEach((target: any) => {
      refine(target.sourceIdentifier, target.key);
    });
    return;
  }

  targets.forEach((target: any) => {
    if (!isTermActive(target)) {
      refine(target.sourceIdentifier, target.key);
    }
  });
};

const GroupedAccordion: React.FC<GroupedAccordionProps> = ({
  groupedAttributes = [],
  activeRefineList,
  refine,
  categories,
  tagsToShow,
  loadMoreText,
  seeLessText,
}) => {
  const firstOpenKey = useMemo(() => getFirstOpenKey(groupedAttributes), [groupedAttributes]);
  const { wishlist } = useProductList();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [openKeys, setOpenKeys] = useState<string[]>(() => (firstOpenKey ? [firstOpenKey] : []));

  const initialized = useRef(false);

  const { enableSortSize, enableFacetPagination } = useFeatures();
  const isSizeSortEnabled = enableSortSize();
  const isFacetPaginationEnabled = enableFacetPagination();

  useEffect(() => {
    if (!initialized.current) {
      setOpenKeys(firstOpenKey ? [firstOpenKey] : []);
      initialized.current = true;
      return;
    }

    setOpenKeys((prev) => {
      const validPrev = prev.filter((key) => hasValidGroupTerms(groupedAttributes, key));

      if (validPrev.length === 0) {
        return firstOpenKey ? [firstOpenKey] : [];
      }

      return validPrev;
    });
  }, [firstOpenKey, groupedAttributes]);

  const toggleAccordion = (groupKey: string) => {
    setOpenKeys((prev) => (prev.includes(groupKey) ? prev.filter((k) => k !== groupKey) : [...prev, groupKey]));
  };

  const tagsToShowList = useMemo(() => {
    if (!tagsToShow) return [];
    if (Array.isArray(tagsToShow)) return (tagsToShow as string[]).map((t) => t.trim());
    return tagsToShow
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }, [tagsToShow]);

  const tagsToShowLower = useMemo(() => tagsToShowList.map((t) => t.toLowerCase()), [tagsToShowList]);

  const categoryIdToNameMap = useMemo(() => buildCategoryIdToNameMap(categories), [categories]);

  const allCategoryTags = useMemo(() => collectAllCategoryTags(categories), [categories]);

  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({});

  const showMoreTerms = (groupKey: string) => {
    setVisibleCounts((prev) => ({ ...prev, [groupKey]: (prev[groupKey] ?? FACETS_PAGE_SIZE) + FACETS_PAGE_SIZE }));
  };

  /* Collapse back to the initial page of terms. */
  const showLessTerms = (groupKey: string) => {
    setVisibleCounts((prev) => ({ ...prev, [groupKey]: FACETS_PAGE_SIZE }));
  };

  const [announcement, setAnnouncement] = useState('');

  return (
    <div data-testid="grouped-container" className="mt-5">
      <hr className="w-full border-[#EBEBEB]" aria-hidden="true" />
      {groupedAttributes.map((group: any, index: number) => {
        const isTermActive = (term: any) => {
          const termLabel = normalizeValue(term?.label);
          const termKey = normalizeValue(term?.key);

          return activeRefineList.some((active: any) => {
            const a = normalizeValue(active);
            const isProductIdFormat = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(a);
            if (isProductIdFormat) return false;

            return a === termLabel || a === termKey;
          });
        };

        let displayTerms = group?.terms ?? [];
        const groupLabelLower = (group?.label ?? '').toLowerCase();

        const isTagsGroup =
          (groupLabelLower.includes('tags') ||
            displayTerms.some((t: any) => t.sourceIdentifier?.toLowerCase().includes('tags'))) &&
          !groupLabelLower.includes('category') &&
          !groupLabelLower.includes('favorite');

        if (isTagsGroup) {
          const filteredExisting = displayTerms.filter((t: any) => tagsToShowLower.includes(normalizeValue(t.label)));
          const existingLabelsLower = new Set(filteredExisting.map((t: any) => normalizeValue(t.label)));

          const missingTags = tagsToShowList.filter(
            (tag) => !existingLabelsLower.has(tag.toLowerCase()) && allCategoryTags.has(tag.toLowerCase()),
          );

          const sourceIdentifier = 'variants.attributes.categoryTags';
          const additionalTerms = missingTags.map((tag) => ({
            label: tag,
            key: tag,
            sourceIdentifier,
          }));

          displayTerms = [...filteredExisting, ...additionalTerms];
          displayTerms = aggregateOnlyTagsToShow(displayTerms, tagsToShowLower);
        }

        /* Sort Size PP values by area */
        const isSizePPGroup = displayTerms?.some((term: any) =>
          [
            'variants.attributes.size_pp',
            'variants.attributes.book_size_pb',
            'variants.attributes.size_wa',
            'variants.attributes.size_cards',
            'variants.attributes.size_ornaments',
            'variants.attributes.size_calendars',
            'variants.attributes.size_cm',
            'variants.attributes.size_textiles',
            'variants.attributes.size_dd',
          ].includes(term?.sourceIdentifier),
        );

        if (isSizePPGroup && isSizeSortEnabled) {
          displayTerms = [...displayTerms].sort((a: any, b: any) => {
            const valueA = String(a?.label ?? a?.key ?? '');
            const valueB = String(b?.label ?? b?.key ?? '');

            return calculateArea(valueA) - calculateArea(valueB);
          });
        }
        const hasTerms = displayTerms.length > 0;
        if (!hasTerms) return null;
        const groupKey = getGroupKey(group);
        const isOpen = openKeys.includes(groupKey);

        const favoriteTerms = displayTerms.filter((term: any) => term && term.key === 'favorite');
        const selectableTerms = displayTerms.filter((term: any) => term.key !== 'favorite');
        const visibleCount = visibleCounts[groupKey] ?? FACETS_PAGE_SIZE;
        /* Paging needs the flag and a CMS "load more" label, else there is no way to reveal the rest. */
        const isPaged = isFacetPaginationEnabled && Boolean(loadMoreText);
        const visibleTerms = isPaged ? selectableTerms.slice(0, visibleCount) : selectableTerms;
        const hasMoreTerms = selectableTerms.length > visibleTerms.length;
        /* Once every term is on screen, offer a way back to the initial page. */
        const canShowLess =
          isPaged && Boolean(seeLessText) && !hasMoreTerms && selectableTerms.length > FACETS_PAGE_SIZE;

        return (
          <div key={`${groupKey}-${index}`}>
            <button
              data-testid={`grouped-group-${index}`}
              type="button"
              onClick={() => toggleAccordion(groupKey)}
              className="w-full text-left text-16"
              style={{ cursor: 'pointer', padding: '8px' }}
              data-ffid={`lbl-facets-${cleanString(group?.label ?? '')}`}
              aria-expanded={isOpen}
            >
              {group?.label}
              <span style={{ float: 'right' }} data-ffid={`icn-facets-${cleanString(group?.label ?? '')}`}>
                {isOpen ? (
                  <ChevronUpIcon className="w-16" data-testid="chevron-up" />
                ) : (
                  <ChevronDownIcon className="w-16" data-testid="chevron-down" />
                )}
              </span>
            </button>

            {/* Accordion Content */}
            {isOpen && (
              <div style={{ padding: '8px' }}>
                {visibleTerms.map((term: any, ind: number) => {
                  const isAggregatedTag = isTagsGroup && Array.isArray(term?.refineTargets);

                  const targets = isAggregatedTag ? term.refineTargets : [term];
                  const activeTargets = targets.filter((t: any) => isTermActive(t));
                  const isChecked = activeTargets.length > 0;

                  let finalCleanLabel = term.label ? term.label.toString() : '';
                  const isRawIdFormat = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                    finalCleanLabel,
                  );

                  if (isRawIdFormat && categoryIdToNameMap[finalCleanLabel.toLowerCase()]) {
                    finalCleanLabel = categoryIdToNameMap[finalCleanLabel.toLowerCase()];
                  }

                  const elementKey = term?.key || `term-${ind}`;

                  return (
                    <div key={`${elementKey}-${ind}`} className="mb-6" data-testid={`grouped-terms-${ind}`}>
                      <Checkbox
                        checked={isChecked}
                        onChange={() => {
                          if (isAggregatedTag) {
                            applyAggregatedRefinement({
                              isChecked,
                              activeTargets,
                              targets,
                              isTermActive,
                              refine,
                            });
                            return;
                          }
                          const nextState = !isChecked;
                          setAnnouncement(`${finalCleanLabel} ${nextState ? 'checked' : 'unchecked'}`);
                          refine(term.sourceIdentifier, term.key);
                        }}
                        label={finalCleanLabel}
                        labelPosition="on-right"
                        className="bg-green-800"
                        aria-label={finalCleanLabel}
                        role="checkbox"
                        aria-checked={isChecked}
                      />
                    </div>
                  );
                })}

                {favoriteTerms.map((term: any, ind: number) => {
                  const isChecked = Boolean(searchParams.get('wishListIds'));
                  const elementKey = term?.key || 'favorite-toggle';

                  return (
                    <div key={`${elementKey}-${ind}`} className="mb-6" data-testid={`grouped-terms-${ind}`}>
                      <Checkbox
                        checked={isChecked}
                        onChange={() => {
                          const params = new URLSearchParams(searchParams.toString());

                          if (isChecked) {
                            params.delete('wishListIds');
                            router.replace(`?${params.toString()}`);
                          } else {
                            const productIds = getWishlistProductIds(wishlist);

                            if (productIds.length > 0) {
                              params.set('wishListIds', productIds.join(','));
                            }

                            router.replace(`?${params.toString()}`);
                          }
                        }}
                        label={term.label ? term.label.toString() : ''}
                        labelPosition="on-right"
                        className="bg-green-800"
                      />
                    </div>
                  );
                })}

                {hasMoreTerms && (
                  <button
                    type="button"
                    onClick={() => showMoreTerms(groupKey)}
                    className="mt-4 text-14 font-semibold text-green-800 underline"
                    data-testid={`grouped-load-more-${index}`}
                    data-ffid={`btn-load-more-facets-${cleanString(group?.label ?? '')}`}
                  >
                    {loadMoreText}
                  </button>
                )}

                {canShowLess && (
                  <button
                    type="button"
                    onClick={() => showLessTerms(groupKey)}
                    className="mt-4 text-14 font-semibold text-green-800 underline"
                    data-testid={`grouped-see-less-${index}`}
                    data-ffid={`btn-see-less-facets-${cleanString(group?.label ?? '')}`}
                  >
                    {seeLessText}
                  </button>
                )}
              </div>
            )}

            <hr className="w-full border-[#EBEBEB]" />
          </div>
        );
      })}

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
    </div>
  );
};

export default GroupedAccordion;
